import React, { useRef, useEffect } from 'react';

interface MatrixRainProps {
  density?: number; // 0.01 to 0.1, default 0.02
  speed?: number; // 0.5 to 2, default 1
  fontSize?: number; // Default 14
  opacity?: number; // 0 to 1, default 0.8
  highlightProbability?: number; // 0 to 1, default 0.05
}

const MatrixRain: React.FC<MatrixRainProps> = ({
  density = 0.02,
  speed = 1,
  fontSize = 14,
  opacity = 0.8,
  highlightProbability = 0.05
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    // Clamp values to reasonable ranges
    const clampedDensity = Math.max(0.01, Math.min(0.1, density));
    const clampedSpeed = Math.max(0.5, Math.min(2, speed));
    const clampedFontSize = Math.max(10, Math.min(30, fontSize));
    const clampedOpacity = Math.max(0.1, Math.min(1, opacity));
    const clampedHighlightProb = Math.max(0, Math.min(0.2, highlightProbability));
    
    // Matrix characters (can be customized)
    const matrixChars = '01アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン';
    
    // Calculate columns based on font size and density
    const columns = Math.floor(canvas.width / clampedFontSize * clampedDensity);
    const drops: number[] = [];
    const highlights: boolean[] = [];
    const glitches: boolean[] = [];
    const speeds: number[] = [];
    const charSets: number[] = [];
    
    // Initialize drops at random positions
    for (let i = 0; i < columns; i++) {
      drops[i] = Math.random() * canvas.height;
      highlights[i] = Math.random() < clampedHighlightProb;
      glitches[i] = Math.random() < 0.05;
      speeds[i] = (Math.random() * 0.5 + 0.75) * clampedSpeed;
      charSets[i] = Math.random() < 0.7 ? 0 : 1; // 0: matrix, 1: binary only
    }
    
    // Create glitch effect on some characters
    const createGlitchEffect = () => {
      for (let i = 0; i < columns; i++) {
        if (Math.random() < 0.01) {
          glitches[i] = !glitches[i];
        }
      }
      
      // Randomly change character sets for some drops
      for (let i = 0; i < columns; i++) {
        if (Math.random() < 0.005) {
          charSets[i] = Math.random() < 0.7 ? 0 : 1;
        }
      }
      
      // Change highlight columns occasionally
      for (let i = 0; i < columns; i++) {
        if (Math.random() < 0.01) {
          highlights[i] = Math.random() < clampedHighlightProb * 2;
        }
      }
    };
    
    const draw = () => {
      // Add semi-transparent black fill to create trail effect
      ctx.fillStyle = `rgba(0, 0, 0, ${0.04 * clampedSpeed})`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Set font
      ctx.font = `${clampedFontSize}px monospace`;
      ctx.textAlign = 'center';
      
      // Create glitch effect randomly
      if (Math.random() < 0.05) {
        createGlitchEffect();
      }
      
      // Draw characters
      for (let i = 0; i < drops.length; i++) {
        const x = i * (canvas.width / columns);
        
        // Choose character from the set
        const chars = charSets[i] === 0 ? matrixChars : '01';
        const char = chars.charAt(Math.floor(Math.random() * chars.length));
        
        // Determine the color of the character
        if (highlights[i]) {
          // Highlighted characters (brighter)
          ctx.fillStyle = `rgba(220, 255, 220, ${clampedOpacity + 0.2})`;
          ctx.shadowColor = '#00ff00';
          ctx.shadowBlur = 10;
        } else if (glitches[i] && Math.random() < 0.5) {
          // Glitched characters (occasional color shift)
          const hue = Math.random() < 0.5 ? 120 : Math.random() * 60 + 90;
          ctx.fillStyle = `hsla(${hue}, 100%, 50%, ${clampedOpacity})`;
          ctx.shadowColor = `hsla(${hue}, 100%, 50%, 0.8)`;
          ctx.shadowBlur = Math.random() < 0.5 ? 5 : 0;
        } else {
          // Regular characters
          const alpha = Math.random() * 0.5 + clampedOpacity - 0.3;
          ctx.fillStyle = `rgba(0, 255, 0, ${alpha})`;
          ctx.shadowBlur = 0;
        }
        
        // Draw the character
        const y = drops[i] * clampedFontSize;
        
        // Apply a glitch effect to some characters by offsetting them
        if (glitches[i] && Math.random() < 0.3) {
          const offset = (Math.random() - 0.5) * 6;
          ctx.fillText(char, x + offset, y);
        } else {
          ctx.fillText(char, x, y);
        }
        
        // Reset when off-screen and randomize reset height
        if (y > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        
        // Move the drop down
        drops[i] += speeds[i] * 0.05;
      }
      
      // Occasionally add a horizontal glitch line
      if (Math.random() < 0.01) {
        const y = Math.random() * canvas.height;
        const height = Math.random() * 5 + 1;
        const glitchWidth = Math.random() * canvas.width * 0.8;
        const x = (canvas.width - glitchWidth) * Math.random();
        
        ctx.fillStyle = 'rgba(0, 255, 100, 0.8)';
        ctx.fillRect(x, y, glitchWidth, height);
        
        // Add a subtle shadow glow
        ctx.shadowColor = '#00ff00';
        ctx.shadowBlur = 10;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
      }
      
      // Reset shadow
      ctx.shadowBlur = 0;
    };
    
    // Handle window resize
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      
      // Recalculate columns
      const newColumns = Math.floor(canvas.width / clampedFontSize * clampedDensity);
      
      // Adjust drops array if needed
      if (newColumns > columns) {
        // Add new drops if canvas got bigger
        for (let i = columns; i < newColumns; i++) {
          drops[i] = Math.random() * canvas.height;
          highlights[i] = Math.random() < clampedHighlightProb;
          glitches[i] = Math.random() < 0.05;
          speeds[i] = (Math.random() * 0.5 + 0.75) * clampedSpeed;
          charSets[i] = Math.random() < 0.7 ? 0 : 1;
        }
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    // Animation loop
    let animationFrameId: number;
    
    const animate = () => {
      draw();
      animationFrameId = requestAnimationFrame(animate);
    };
    
    animate();
    
    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [density, speed, fontSize, opacity, highlightProbability]);
  
  return (
    <canvas
      ref={canvasRef}
      className="background-canvas"
      style={{
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none'
      }}
    />
  );
};

export default MatrixRain;